/**
 * Fleet payout run sheet (TMMPDM-0010 / 0020): GET /v1/admin/payouts + CSV + record.
 * TMMPDM-0037: the status toggle gains Charged (`status=CHARGED`, tmm-api 0099); the CSV follows it.
 * TMMPDM-0035: `status` and `reseller` live in the URL; the scope and the statement summary sit
 * under the heading; "Exports {{status}} statements" beside Download CSV (TBL-015 — the CSV is
 * requested with the visible status and reseller).
 * TMMPDM-0095 / 0096: when the old payout actions are retired the sheet is frozen history: a banner
 * says so, and Record payout and Release hold are gone. Before that, nothing changes.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, positiveInt, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { ErrorState, PageHeader, useNotify } from '@/components/page';
import { formatMoney } from '@/format';
import { adminApi } from '@/api/tmm/admin';
import { PAYOUT_STATUSES, type PayoutStatementView, type PayoutStatus } from '@/api/tmm/types';
import { useRunSheet } from './payouts/useRunSheet';
import { RunSheetTable } from './payouts/RunSheetTable';
import { RecordPayoutDialog } from './payouts/RecordPayoutDialog';
import { saveBlob } from './payouts/downloadCsv';
import { holdCount, payableByCurrency } from './payouts/payableByCurrency';
import { FrozenHistoryBanner } from './ledger/ledgerShared';
import { usePayoutActionsRetired } from './ledger/ledger';
import {
  resellerFilterOptions,
  resellerScope,
  useResellerNames,
} from './resellers/useResellerNames';

type RunSheetView = { status: PayoutStatus; reseller: number | undefined };
const VIEW: UrlSchema<RunSheetView> = {
  status: { default: 'PENDING', parse: oneOf(PAYOUT_STATUSES) },
  reseller: { default: undefined, parse: positiveInt },
};
const DEFAULT_VIEW: RunSheetView = { status: 'PENDING', reseller: undefined };

function todayIso(today: Date = new Date()): string {
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function Payouts() {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  const { status, reseller: resellerId } = view;
  const { resellers, names: resellerNames } = useResellerNames();
  const { data, loading, error, failure, refetch } = useRunSheet(status, resellerId);
  const payoutActions = usePayoutActionsRetired();
  const frozen = payoutActions.retired;
  const movedToLedger = () => {
    payoutActions.reload();
    refetch();
  };
  const [recording, setRecording] = useState<PayoutStatementView | null>(null);
  const [csvError, setCsvError] = useState(false);
  // TMMPDM-0044: a payout the server refused — said here once the dialog closes and the sheet re-reads.
  const [refusal, setRefusal] = useState<string | null>(null);

  const rows = data?.payouts ?? null;
  const unattributed = data?.unattributed ?? [];
  const payableFigures = rows
    ? payableByCurrency(rows)
        .map((entry) => formatMoney(entry.payableMinor, entry.currency, locale))
        .join(' · ')
    : '';
  const holds = rows ? holdCount(rows) : 0;
  const filtered = status !== DEFAULT_VIEW.status || resellerId !== undefined;
  const statusWord = t(`payouts.status.${status}`, status).toLowerCase();

  const unattributedAmounts = unattributed
    .map((entry) =>
      t('payouts.unattributed.row', '{{rows}} rows · {{retail}}', {
        rows: entry.rows,
        retail: formatMoney(entry.retailMinor, entry.currency, locale),
      }),
    )
    .join(' · ');

  const download = async () => {
    setCsvError(false);
    try {
      const blob = await adminApi.payoutRunSheetCsv(status, resellerId);
      saveBlob(blob, `payouts-${status}-${todayIso()}.csv`);
    } catch {
      setCsvError(true);
    }
  };

  return (
    <Box>
      <PageHeader
        title={t('page.payouts.title', 'Payouts')}
        subtitle={t(
          'page.payouts.subtitle',
          'One run sheet for resellers and affiliates across every tenant.',
        )}
        scope={resellerScope(t, resellerNames, resellerId)}
        meta={
          rows ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t(
                'payouts.summary',
                '{{count}} statements · {{payable}} payable · {{holds}} on hold',
                {
                  count: rows.length,
                  payable: payableFigures,
                  holds,
                },
              )}
            </Typography>
          ) : null
        }
        controls={
          <Typography variant="caption" color="text.secondary">
            {t('payouts.exportsStatus', 'Exports {{status}} statements', { status: statusWord })}
          </Typography>
        }
        secondaryActions={[
          { label: t('payouts.downloadCsv', 'Download CSV'), onClick: () => void download() },
        ]}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' }, mb: 2 }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={status}
          onChange={(_event, value: PayoutStatus | null) => {
            if (value) setView({ status: value });
          }}
          aria-label={t('payouts.status.groupAria', 'Payout status')}
        >
          {PAYOUT_STATUSES.map((value) => (
            <ToggleButton key={value} value={value}>
              {t(`payouts.status.${value}`, value)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="payouts-reseller-label">{t('partners.singular', 'Partner')}</InputLabel>
          <Select
            labelId="payouts-reseller-label"
            value={resellerId === undefined ? 'ALL' : String(resellerId)}
            label={t('partners.singular', 'Partner')}
            onChange={(event) =>
              setView({
                reseller: event.target.value === 'ALL' ? undefined : Number(event.target.value),
              })
            }
          >
            <MenuItem value="ALL">{t('dashboard.resellerFilter.all', 'All')}</MenuItem>
            {resellerFilterOptions(t, resellers, resellerId).map((reseller) => (
              <MenuItem key={reseller.id} value={String(reseller.id)}>
                {reseller.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {frozen ? <FrozenHistoryBanner /> : null}
      {error ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={failure}
            message={t('payouts.loadError', 'Could not load the payout run sheet.')}
            onRetry={refetch}
          />
        </Box>
      ) : null}
      {refusal ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRefusal(null)}>
          {refusal}
        </Alert>
      ) : null}
      {csvError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('payouts.csvError', 'Could not download the CSV.')}
        </Alert>
      ) : null}
      {unattributed.length > 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'payouts.unattributed.alert',
            'Revenue not yet attributed to a reseller: {{amounts}}',
            { amounts: unattributedAmounts },
          )}
        </Alert>
      ) : null}

      <RunSheetTable
        rows={error ? null : rows}
        loading={loading}
        locale={locale}
        emptyLabel={t('payouts.empty', 'No payout statements.')}
        onClearFilters={filtered ? () => setView(DEFAULT_VIEW) : undefined}
        onRecord={setRecording}
        onHoldReleased={() => {
          setRefusal(null);
          refetch();
        }}
        frozen={frozen}
        onMovedToLedger={movedToLedger}
      />

      <RecordPayoutDialog
        open={recording !== null}
        row={recording}
        onClose={() => setRecording(null)}
        onRecorded={() => {
          setRecording(null);
          setRefusal(null);
          notify.success(t('payouts.record.recorded', 'Payout recorded.'));
          refetch();
        }}
        onRefused={(message) => {
          setRecording(null);
          setRefusal(message);
          refetch();
        }}
        onMovedToLedger={movedToLedger}
      />
    </Box>
  );
}
